<template>
  <li>
    <div class="formLineBox -declaracao">
      <fieldset
        class="formLine formLine--produto"
        :class="{ active: self.name, error: $v.self.name.$error}"
      >
        <div class="inputBox">
          <label :for="setFieldIdentifier('name')">Item{{isNameRequired ? '*' : ''}}</label>
          <autocomplete
            ref="name"
            v-model="self.name"
            label="name"
            url="/api/v2/me/products/search"
            anchor="name"
            :id="setFieldIdentifier('name')"
            :name="setFieldIdentifier('name')"
            :debounce="1000"
            :onShouldRenderChild="renderChild"
            :customHeaders="{ 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-CSRF-TOKEN': csrf.content }"
            :onInput="setData"
            :onSelect="getData"
            :class="{'autocomplete-wrapper--disabled': readOnly}"
          />

          <p v-if="!$v.self.name.required" class="errorText">
            Campo obrigatório.
          </p>

          <p v-if="!$v.self.name.minLength" class="errorText">
            Digite pelo menos 4 caracteres!
          </p>
        </div>
      </fieldset>

      <fieldset
        class="formLine formLine--valor"
        :class="{active: self.unitary_value, error: $v.self.unitary_value.$error}"
      >
        <div class="inputBox ipt-currency">
          <label :for="setFieldIdentifier('unitary_value')">Valor unitário{{isPriceRequired ? '*' : ''}}</label>
          <v-money
            :style="$v.self.unitary_value.$error ? 'color: #E7505A;' : 'color: #3B3F51;'"
            v-model="self.unitary_value"
            v-bind="money"
            :name="setFieldIdentifier('unitary_value')"
            :id="setFieldIdentifier('unitary_value')"
            :maxlength="String(serviceMaxInsuranceValue).length + 4"
            masked
            onkeypress="return (event.charCode == 8 || event.charCode == 0) ? null : event.charCode >= 48 && event.charCode <= 57"
            :disabled="readOnly"
          />

          <p v-if="!$v.self.unitary_value.maxValue" class="errorText">
            Valor excede limite de {{beautifyNumberToBRL(serviceMaxInsuranceValue)}}
          </p>

          <p v-if="!$v.self.unitary_value.required || !$v.self.unitary_value.minValue" class="errorText">
            Campo obrigatório.
          </p>
        </div>
      </fieldset>

      <fieldset
        class="formLine formLine--quantidade"
        :class="{active: self.quantity, error: $v.self.quantity.$error}"
      >
        <div class="inputBox">
          <label :for="setFieldIdentifier('quantity')">Quantidade{{isQuantityRequired ? '*' : ''}}</label>
          <input
            ref="quantity"
            type="text"
            v-model="self.quantity"
            v-mask="'999'"
            :name="setFieldIdentifier('quantity')"
            :id="setFieldIdentifier('quantity')"
            :disabled="readOnly"
          >

          <p v-if="!$v.self.quantity.required" class="errorText">
            Campo obrigatório.
          </p>

          <p v-if="$v.self.quantity.required && !$v.self.quantity.minValue" class="errorText">
            Digite um valor válido!
          </p>
        </div>
      </fieldset>

      <transition name="fade">
        <fieldset
          class="formLine formLine--remove"
          v-if="!readOnly"
        >
          <a
            v-if="trashRemover"
            class="removeItem"
            href="javascript:;"
            @click.prevent="removeItem(index)"
          >
            <svg class="ico">
              <use
                xlink:href="/images/svg-symbols.svg#trash"
              ></use>
            </svg>
          </a>
        </fieldset>
      </transition>
    </div>
  </li>
</template>

<script>
  import {
    requiredIf,
    minLength,
    minValue,
    maxValue,
    numeric
  } from 'vuelidate/lib/validators';
  import {Money} from 'v-money'
  import AwesomeMask from 'awesome-mask';
  import Autocomplete from 'vue2-autocomplete-js';
  import {
    BRLtoNumber,
    beautifyNumberToBRL
  } from 'commons/js/formatters/Currency';

  export default {
    directives: {
      'mask': AwesomeMask
    },

    components: {
      Autocomplete,
      'v-money': Money
    },

    props: {
      allFieldsRequired: {
        type: Boolean,
        default: true
      },

      companyId: {
        type: Number,
      },

      index: {
        type: Number,
        required: true
      },

      size: {
        type: Number,
        required: true
      },

      insuranceValue: {
        type: [String, Number],
        required: true
      },

      serviceMaxInsuranceValue: {
        type: Number,
        required: false,
        default: 50000,
      },

      readOnly: {
        type: Boolean,
        default: false
      }
    },

    validations() {
      return {
        self: {
          name: {
            required: requiredIf(() => this.isNameRequired),
            minLength: minLength(4)
          },

          unitary_value: {
            required: requiredIf(() => this.isPriceRequired),

            minValue(value) {
              if (this.isPriceRequired) {
                return value !== '0,00'
              }

              return true;
            },

            maxValue(value) {
              const formattedValue = BRLtoNumber(value);

              if(this.companyId && this.companyId !== 1) {
                return formattedValue <= this.serviceMaxInsuranceValue;
              }

              return true;
            },
          },

          quantity: {
            required: requiredIf(() => this.isQuantityRequired),
            minValue: minValue(1),
            maxValue: maxValue(999),
            numeric
          }
        }
      }
    },

    computed: {
      trashRemover() {
        if((this.allFieldsRequired || this.hasInsuranceValue) && this.size === 1) {
          return false
        }
        return true
      },
      csrf: function () {
        return document.head.querySelector('meta[name="csrf-token"]');
      },
      self: function () {
        return this.$parent.products[this.index];
      },
      hasValidName() {
        const limitNumbers = 4;
        return !!(this.self && this.self.name.length >= limitNumbers);
      },
      hasValidUnitaryValue() {
        return !!(this.self && this.self.unitary_value !== '0,00');
      },
      hasValidQuantity() {
        return !!(this.self && this.self.quantity > 0);
      },
      hasSomeFieldValid() {
        return !!(this.hasValidName || this.hasValidUnitaryValue || this.hasValidQuantity);
      },
      isNameRequired() {
        return this.allFieldsRequired || this.hasSomeFieldValid;
      },
      isPriceRequired() {
        return ((this.allFieldsRequired || this.hasSomeFieldValid) && this.hasInsuranceValue);
      },
      isQuantityRequired() {
        return this.allFieldsRequired || this.hasSomeFieldValid;
      },
      hasInsuranceValue() {
        return Number(this.insuranceValue) > 0;
      }
    },

    methods: {
      beautifyNumberToBRL,

      setFieldIdentifier(field) {
        return `${field}-${this._uid}`;
      },

      getData(obj) {
        this.self.unitary_value = obj.unitary_value;
        this.self.name = obj.name;

        this.$refs.quantity.focus();
      },

      setData(text) {
        this.self.name = text;
      },

      removeItem() {
        this.$emit('remove');
      },

      validate() {
        if (this.$v.$invalid) {
          this.$v.$touch();

          return false;
        }

        return true;
      },

      renderChild(data) {
        return `<span>${data.name} (${beautifyNumberToBRL(data.unitary_value)})</span>`;
      }
    },

    data() {
      return {
        money: {
          decimal: ',',
          thousands: '.',
          precision: 2,
        },
      }
    },

    mounted() {
      this.$refs.name.$refs.input.maxLength = 255;
      this.$refs.name.setValue(this.self.name);
    }
  }
</script>

<style lang="stylus" scoped>
>>> .autocomplete-wrapper
  &--disabled
    .autocomplete-input
      opacity 0.6
      pointer-events none
</style>
